import { get } from "lodash";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { toast } from "react-toastify";
import { matchApi, markupApi } from "@/api";
import { PIPELINE_STAGE } from "@/constants/pipeline-stage";

export const getObjKeysLength = (obj) => Object.keys(obj).length;

export const putFormValidationFocus = () =>
  document?.querySelectorAll("input.is-invalid")[0].focus();

export const catchErrors = (error, action) => (dispatch) => {
  if (get(error, "response.status")) {
    const errorData = {
      data: { ...error.response.data },
      status: error.response.status,
      url: error.response.config.url,
    };
    dispatch(action(errorData));
  } else {
    throw error;
  }
};

export const getDescriptorsStatus = async (id) => {
  try {
    const { descriptors } = await markupApi.getStatus(id);

    return descriptors ?? false;
  } catch (e) {
    return false;
  }
};

export function generateSuperTrackMap(superTracks) {
  const obj = {};

  superTracks.forEach(({ super_track_uid, tracks_data, id, ...rest }) => {
    obj[super_track_uid] = {
      tracks: tracks_data,
      isHidden: false,
      ...rest,
    };
  });

  return obj;
}

export function generateSuperTrackMapV2(superTracks) {
  const obj = {};

  for (let super_track_uid in superTracks) {
    const { tracks, ...rest } = superTracks[super_track_uid];
    obj[super_track_uid] = {
      tracks,
      isHidden: false,
      ...rest,
    };
  }

  return obj;
}

// TODO: remove this someday
export function createTracksMap(superTracks) {
  const map = new Map();
  superTracks
    .flatMap(({ tracks_data }) => tracks_data)
    .forEach((track) => {
      map.set(track.track_id, track);
    });

  return map;
}

export function createTracksMapV2(supertracks) {
  const map = new Map();

  for (let super_track_uid in supertracks) {
    const { tracks } = supertracks[super_track_uid];
    tracks.forEach((track) => {
      map.set(track.track_id, track);
    });
  }

  return map;
}

export function convertTime2Frame(timestamp, fps) {
  if (!timestamp || !fps) return;
  // Convert the timestamp to seconds
  const timeComponents = timestamp.split(/[:.]/);
  let totalSeconds =
    +timeComponents[0] * 3600 + +timeComponents[1] * 60 + +timeComponents[2];
  if (timeComponents.length === 4) {
    totalSeconds += +timeComponents[3] / 1000000;
  }
  // Calculate the frame number
  const frameNumber = Math.floor(totalSeconds * fps);
  return frameNumber;
}

export function frameToTime(currentFrame, fps) {
  var totalSeconds = currentFrame / fps;
  var hours = Math.floor(totalSeconds / 3600);
  var minutes = Math.floor((totalSeconds % 3600) / 60);
  var seconds = Math.floor(totalSeconds % 60);
  var milliseconds = Math.floor((totalSeconds % 1) * 1000000);

  var timeString =
    (hours < 10 ? "0" : "") +
    hours +
    ":" +
    (minutes < 10 ? "0" : "") +
    minutes +
    ":" +
    (seconds < 10 ? "0" : "") +
    seconds +
    "." +
    milliseconds.toString().padStart(6, "0");

  return timeString;
}

export function countLargeGaps(frames, frameStep, lastFrame) {
  frames = [-1, ...frames, lastFrame + 1];

  // Step 1: Sort the frame array
  frames.sort((a, b) => a - b);

  // Step 2: Initialize a variable to keep track of the number of large gaps
  let largeGapCount = 0;

  // Step 3: Iterate through frames to find and count gaps larger than frameStep
  for (let i = 0; i < frames.length - 1; i++) {
    let current = frames[i];
    let next = frames[i + 1];

    // Calculate the gap size
    let gapSize = next - current;

    // Step 4: Check if the gap size is larger than frameStep
    if (gapSize > frameStep) {
      largeGapCount++;
    }
  }

  // Return the count of large gaps
  return largeGapCount;
}

export function isJsonString(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

export function cn(...args) {
  return twMerge(clsx(args));
}

function calcDistance(coords1, coords2) {
  // Extract the coordinates for each point
  let [x1, y1] = [(coords1[0] + coords1[2]) / 2, (coords1[1] + coords1[3]) / 2];
  let [x2, y2] = [(coords2[0] + coords2[2]) / 2, (coords2[1] + coords2[3]) / 2];

  // Calculate the Euclidean distance
  const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
  return distance;
}

export function serializeValue(value, minValue, maxValue) {
  return (value - minValue) / (maxValue - minValue);
}

export function getPriorityWeight(
  coords,
  targetCoords,
  maxDistance,
  timeDiff,
  totalFrames
) {
  return (
    serializeValue(calcDistance(coords, targetCoords), 0, maxDistance) * 0.2 +
    serializeValue(timeDiff, 0, totalFrames) * 0.8
  );
}

export async function getTaskLink(matchId, taskType) {
  console.log({ taskType });
  try {
    const { id, task } = await matchApi.postMatchTask(matchId, taskType);
    if (!task) {
      throw new Error(`There is no ${taskType} task for this match.`);
    }
    if (taskType === "supertrack") {
      return `/panel/markup/super-tracks/${matchId}/markup/${task.super_track_uid}?taskId=${id}`;
    } else if (taskType === "validate") {
      const { frame_start_id, frame_stop_id } = task;
      return `/panel/events-validation/${matchId}?taskId=${id}&frame_start_id=${frame_start_id}&frame_stop_id=${frame_stop_id}`;
    } else if (taskType === "ball_supertrack") {
      return `/panel/markup/super-tracks/${matchId}/markup/${task.super_track_uid}?taskId=${id}&type=ball&frame_start=${task.frame_start}&frame_end=${task.frame_end}`;
    } else if (taskType === "match_event") {
      return `/panel/event-light/${matchId}?taskId=${id}`;
    } else if (taskType === "event_tagging") {
      return `/panel/events-tagging/${matchId}?taskId=${id}`;
    }
  } catch (err) {
    console.log(err);
    if (err.response) {
      if (err.response.status === 404) toast.warn("No more tasks.");
      else if (err.response.data?.non_field_errors)
        toast.warn(err.response.data?.non_field_errors.join(", "));
    } else if (err.message) {
      toast.warn(err.message);
    } else {
      toast.error("Sorry, Something went wrong.");
    }
  }
}

export async function getPathForStage(stage, matchId) {
  console.log({ stage });
  switch (stage) {
    case PIPELINE_STAGE.preparation:
      return `/panel/markup/field/${matchId}`;
    case PIPELINE_STAGE.done:
    case PIPELINE_STAGE.ballSupertrack: {
      const link = await getTaskLink(matchId, "ball_supertrack");
      return link;
    }
    case PIPELINE_STAGE.superTracks: {
      const link = await getTaskLink(matchId, "supertrack");
      return link;
    }
    case PIPELINE_STAGE.ballCoords:
      return `/panel/markup/ball/${matchId}`;
    case PIPELINE_STAGE.eventConflict:
      return `/panel/events-resolving/${matchId}`;
    case PIPELINE_STAGE.ballTouch:
      return `/panel/ball-touch/${matchId}`;
    case PIPELINE_STAGE.eventValidation: {
      const link = await getTaskLink(matchId, "validate");
      return link;
    }
    case PIPELINE_STAGE.matchEvent: {
      const link = await getTaskLink(matchId, "match_event");
      return link;
    }
    case PIPELINE_STAGE.eventTagging: {
      const link = await getTaskLink(matchId, "event_tagging");
      return link;
    }
    default:
      throw new Error(`Unknown stage ${stage} for getting redirect path`);
  }
}

export function includesAllElements(mainArray, subArray) {
  return subArray.every((element) => mainArray.includes(element));
}

export function isTimeInRange(time, ranges) {
  for (const range of ranges) {
    const [start, end] = range;
    if (time >= start && time <= end) {
      return true; // Time is within this range
    }
  }
  return false; // Time is not in any range
}

export function getTaskDisplayName(task) {
  if (task.task?.pipeline_passing === "match_analytics") {
    return task.task.supertrack_kind;
  } else {
    if (task.task?.team_name) {
      return task.task?.team_name;
    } else if (task.task.supertrack_kind === "left_team") {
      return "first_team";
    } else if (
      task.task.supertrack_kind === "first_team" ||
      task.task.supertrack_kind === "second_team"
    ) {
      return task.task.supertrack_kind;
    } else {
      return "second_team";
    }
  }
}

export function convertSecondsToTimestamp(timeInSeconds) {
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds % 3600) / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  const milliseconds = Math.floor((timeInSeconds % 1) * 1000000);
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}.${milliseconds
    .toString()
    .padStart(6, "0")}`;
}
